import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Grid, Paper, Typography, CircularProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { TripData, UserActivity, KPIMetrics, AppOpenData } from './types';
import moment from 'moment';

interface GeneralStatisticsProps {
  tripsData: TripData[];
  userActivityData: UserActivity[];
  appOpenData: AppOpenData | null;
  loading?: boolean;
}

interface HourlyActivity {
  hour: string;
  trips: number;
  appOpens: number;
}

const GeneralStatistics: React.FC<GeneralStatisticsProps> = ({ tripsData, userActivityData, appOpenData, loading = false }) => {
  const theme = useTheme();

  const kpiMetrics = useMemo((): KPIMetrics => {
    const totalTrips = tripsData.length;
    const avgDistance = totalTrips > 0 
      ? tripsData.reduce((sum, trip) => sum + trip.distance, 0) / totalTrips 
      : 0;
    const avgDuration = totalTrips > 0 
      ? tripsData.reduce((sum, trip) => sum + trip.duration, 0) / totalTrips 
      : 0;
    const avgBatteryDrain = totalTrips > 0 
      ? tripsData.reduce((sum, trip) => sum + (trip.batteryStart - trip.batteryEnd), 0) / totalTrips 
      : 0;
    
    const totalAppOpens = appOpenData?.totalOpens || 0;
    const opensWithoutRent = totalAppOpens - totalTrips;

    return {
      totalTrips,
      avgDistance: Math.round(avgDistance * 100) / 100,
      avgDuration: Math.round(avgDuration),
      avgBatteryDrain: Math.round(avgBatteryDrain * 10) / 10,
      totalAppOpens,
      opensWithoutRent
    };
  }, [tripsData, appOpenData]);

  const hourlyData = useMemo((): HourlyActivity[] => {
    const hourlyMap = new Map<number, { trips: number; appOpens: number }>();

    for (let hour = 0; hour < 24; hour++) {
      hourlyMap.set(hour, { trips: 0, appOpens: 0 });
    }

    console.log('📊 AI Analytics - Total trips:', tripsData.length);
    console.log('📊 AI Analytics - Total app opens:', userActivityData.length);

    // Перевірка унікальних днів
    const uniqueDays = new Set(tripsData.map(trip => moment(trip.startTime).format('YYYY-MM-DD')));
    console.log('📊 AI Analytics - Unique days in data:', Array.from(uniqueDays));

    tripsData.forEach(trip => {
      const hour = (moment(trip.startTime).hour() + 3) % 24;
      const data = hourlyMap.get(hour)!;
      data.trips++;
    });

    userActivityData.forEach(activity => {
      const hour = (moment(activity.timestamp).hour() + 3) % 24;
      const data = hourlyMap.get(hour)!;
      data.appOpens++;
    });

    const result = Array.from(hourlyMap.entries())
      .map(([hour, data]) => ({
        hour: `${hour}:00`,
        trips: data.trips,
        appOpens: data.appOpens
      }))
      .sort((a, b) => parseInt(a.hour) - parseInt(b.hour));

    console.log('📊 AI Analytics - Hourly data:', result);

    return result;
  }, [tripsData, userActivityData]);

  const KPICard = ({ title, value, unit = '' }: { title: string; value: number | string; unit?: string }) => (
    <Paper
      sx={{
        p: 2,
        textAlign: 'center',
        border: theme.palette.mode === 'light' ? '1px solid #E0E0E0' : 'none',
        borderRadius: 2,
        backgroundColor: theme.palette.mode === 'light' ? '#FAFAFA' : '#2A2A2A',
        minHeight: 90,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center'
      }}
    >
      <Typography
        sx={{
          fontSize: 12,
          color: theme.palette.mode === 'light' ? '#757575' : '#9E9E9E',
          mb: 1
        }}
      >
        {title}
      </Typography>
      {loading ? (
        <CircularProgress size={24} sx={{ margin: '0 auto' }} />
      ) : (
        <Typography
          sx={{
            fontSize: 24,
            fontWeight: 600,
            color: theme.palette.mode === 'light' ? '#424242' : '#FFFFFF'
          }}
        >
          {value}{unit}
        </Typography>
      )}
    </Paper>
  );

  if (tripsData.length === 0 && userActivityData.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 5 }}>
        <Typography sx={{ color: theme.palette.mode === 'light' ? '#757575' : '#9E9E9E' }}>
          Завантажте дані для перегляду статистики
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <KPICard title="Кількість поїздок" value={kpiMetrics.totalTrips} />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <KPICard title="Середня відстань" value={kpiMetrics.avgDistance} unit=" км" />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <KPICard title="Середня тривалість" value={kpiMetrics.avgDuration} unit=" хв" />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <KPICard title="Втрата батареї" value={kpiMetrics.avgBatteryDrain} unit="%" />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <KPICard title="Відкриттів додатку" value={kpiMetrics.totalAppOpens} />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <KPICard 
            title="Відкриттів без оренди" 
            value={kpiMetrics.opensWithoutRent} 
          />
        </Grid>
      </Grid>

      <Paper
        sx={{
          p: 3,
          border: theme.palette.mode === 'light' ? '1px solid #E0E0E0' : 'none',
          borderRadius: 2
        }}
      >
        <Typography
          variant='h6'
          sx={{
            fontWeight: 600,
            fontSize: 16,
            color: theme.palette.mode === 'light' ? '#424242' : '#FFFFFF',
            mb: 2
          }}
        >
          Активність по годинах
        </Typography>

        <Box sx={{ width: '100%', height: 400 }}>
          <ResponsiveContainer width='100%' height='100%'>
            <LineChart
              data={hourlyData}
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid
                strokeDasharray='3 3'
                stroke={theme.palette.mode === 'light' ? '#E0E0E0' : '#424242'}
              />
              <XAxis
                dataKey='hour'
                tick={{
                  fill: theme.palette.mode === 'light' ? '#616161' : '#B0B0B0',
                  fontSize: 12
                }}
              />
              <YAxis
                tick={{
                  fill: theme.palette.mode === 'light' ? '#616161' : '#B0B0B0',
                  fontSize: 12
                }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: theme.palette.mode === 'light' ? '#FFF' : '#424242',
                  border: `1px solid ${theme.palette.mode === 'light' ? '#E0E0E0' : '#616161'}`,
                  borderRadius: 4
                }}
              />
              <Legend />
              <Line
                type='monotone'
                dataKey='trips'
                name='Поїздки'
                stroke='#5073F8'
                strokeWidth={2}
                dot={{ fill: '#5073F8' }}
              />
              <Line
                type='monotone'
                dataKey='appOpens'
                name='Відкриття додатку'
                stroke='#FF9800'
                strokeWidth={2}
                dot={{ fill: '#FF9800' }}
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </Paper>
    </Box>
  );
};

export default GeneralStatistics;
